<template>
  <o-form-group
    :id="this_id"
    :form-state="formState"
    :label="label"
    :message="errorMessages"
    :nolabel="nolabel"
    :type="{ 'is-danger': hasError }"
  >
    <div class="input-group">
      <input
        :id="inputId"
        ref="inputEl"
        type="text"
        class="form-control"
        :class="{
          'is-invalid': dirty && !isValid,
          'is-valid': dirty && isValid,
        }"
        :disabled="_is_readonly"
        v-model="inputValue"
        @input="onInputChange"
        :placeholder="inputFormat"
      />
      <b-input-group-append>
        <b-form-datepicker
          v-model="calendarDate"
          button-only
          right
          locale="en-US"
          aria-controls="example-input"
          @context="onContext"
          @input="onCalendarSelect"
          :start-weekday="firstDayOfWeek"
          :min="minDate"
          :max="maxDate"
          :disabled="_is_readonly"
        ></b-form-datepicker>
        <button
          type="button"
          class="btn-group btn btn-secondary"
          aria-label="Clear"
          :disabled="_is_readonly"
          @click="clear"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </b-input-group-append>
    </div>
  </o-form-group>
</template>

<script>
import moment from "moment";
import OFormGroup from "vue-ocom/components/forms/OFormGroup.vue";
import { BDropdown, BFormDatepicker, BButtonClose } from "bootstrap-vue";
import FormComponentMixin from "@/vue-ocom/mixins/form_component_mixin";

export default {
  name: "DateInput",
  props: {
    value: String,
    inputFormat: {
      type: String,
      default: "D/M/YYYY",
    },
    valueFormat: {
      type: String,
      default: "YYYY-MM-DD",
    },
    minDate: {
      value: [String, Date],
      default: null,
    },
    maxDate: {
      value: [String, Date],
      default: null,
    },
    firstDayOfWeek: {
      type: Number,
      default: 1,
    },
  },
  mixins: [FormComponentMixin],
  components: [OFormGroup, BDropdown, BFormDatepicker, BButtonClose],
  data() {
    return {
      inputValue: this.value ? moment(this.value).format(this.inputFormat) : "",
      calendarDate: this.value ? moment(this.value).format("YYYY-MM-DD") : null,
      showDatepicker: false,
      inputId: "date-input-" + Math.random().toString(36).substr(2, 8),
      isValid: true,
      dirty: false,
      wasValid: true,
      parsed: null, // The parsed Moment
    };
  },
  methods: {
    emitInput(val) {
      if (val == null || val == "") {
        this.$emit("input", val);
      } else {
        if (val.isValid()) {
          this.$emit("input", val.format(this.valueFormat));
        }
      }
    },
    onContext(ctx) {
      // The following will be an empty string until a valid date is entered
      this.calendarDate = ctx.selectedYMD;

      if (this.calendarDate != "") {
        this.emitInput(moment(this.calendarDate));
      }
    },
    isDateValid(val) {
      const trimmed = val.trim();

      if (trimmed === "") {
        this.isValid = true;

        this.calendarDate = null;
        return true;
      }

      this.parsed = moment(trimmed, this.inputFormat, true);

      if (this.parsed.isValid()) {
        if (this.minDate && this.parsed.isBefore(this.minDate)) {
          return false;
        }
        if (this.maxDate && this.parsed.isAfter(this.maxDate)) {
          return false;
        }
        return true;
      }
    },
    onInputChange() {
      this.dirty = true;
      if (this.isDateValid(this.inputValue)) {
        this.isValid = true;
        this.wasValid = true; // Was Valid

        this.calendarDate = this.parsed.format("YYYY-MM-DD");
        this.emitInput(this.parsed);
      } else {
        this.isValid = false; // Invalid now to set to null

        if (!this.wasValid) {
          // Changed from being valid.
          this.wasValid = false;
          this.emitInput(null);
        }
      }
    },
    onCalendarSelect(selectedDate) {
      if (selectedDate) {
        const parsed = moment(selectedDate, "YYYY-MM-DD");
        this.dirty = true;
        this.inputValue = parsed.format(this.inputFormat);
        this.calendarDate = selectedDate;
        this.isValid = true;

        this.emitInput(parsed);

        // Close the dropdown after selection
        this.showDatepicker = false;

        // Return focus to input
        this.$nextTick(() => {
          this.$refs.inputEl.focus();
        });
      }
    },
    clear() {
      this.isValid = true;
      this.calendarDate = null;
      this.inputValue = "";
      this.wasValid = true;
      this.emitInput(null);
    },
  },
  watch: {
    calendarDate(newVal) {
      if (!this.showDatepicker) return;
      if (newVal) {
        const parsed = moment(newVal, "YYYY-MM-DD");
        this.inputValue = parsed.format(this.inputFormat);
        this.isValid = true;

        this.emitInput(parsed);
      }
    },
    value(newVal) {
      const formatted = newVal ? moment(newVal).format(this.inputFormat) : "";
      const calFormatted = newVal ? moment(newVal).format("YYYY-MM-DD") : null;
      if (formatted !== this.inputValue) {
        this.inputValue = formatted;
        this.calendarDate = calFormatted;
        this.onInputChange();
      }
    },
  },
};
</script>
